<template>
  <div class="app-checkout-mnp-data" :class="[this.process]">
    <form>
      <div class="mnp-data--switch">
        <LabeledCustomSwitch name="mnpData" :value.sync="isMnpToggleActive" aria-labelledby="mnp-toggle-headline">
          <template #lbl>
            <h1><slot name="txtHeadline">MNP Toggle Button</slot></h1>
          </template>
        </LabeledCustomSwitch>
      </div>
      <div class="mnp-data--info card" id="mnp-data-infos">
        <div class="heading">
          <h2 class="h5">
            <slot v-if="active" name="txtInfoCardTitleActive"></slot>
            <slot v-else name="txtInfoCardTitleInactive"></slot>
          </h2>
        </div>
        <p>
          <slot v-if="active" name="txtInfoCardDescActive"></slot>
          <slot v-else name="txtInfoCardDescInactive"></slot>
        </p>
      </div>
      <div class="mnp-data--provider">
        <provider-select v-if="active"
                          :process="process"
                          ref="providerSelect"
                          :key="1"
                          :withNetworkSelect="false"
                          :active.sync="active"
                          :brandName.sync="provider.brandName"
                          :network.sync="provider.network"
                          :providerKey.sync="provider.key"
                          :providerCode.sync="provider.code"
                          :providerNetwork.sync="provider.network"
                          :relatedTariff.sync="provider.relatedTariff"
                          :unportableFound.sync="provider.unportable"
                          :invalid.sync="invalid.provider"
                          :preselectProvider="preselectProvider"
                          :quick-selection-brand="quickSelectionBrands"
                          @mnpin-provider-determined="storeSelectedProvider"
                          @clearErrorToast="clearActiveToasts"
                          frontend-context="checkout">
          <template v-for="slot in possibleChildSlotsFor('ProviderSelect')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>

          <template #txtPlaceholderSearchField>
            <slot name="txtPlaceholderSearchFieldProviderSelect"></slot>
          </template>

          <template #txtSearchFieldAltText>
            <slot name="txtSearchFieldAltTextProviderSelect"></slot>
          </template>

          <template #buttonClearProviderAltText>
            <slot name="buttonClearProviderAltTextProviderSelect"></slot>
          </template>
        </provider-select>
      </div>
      <div class="mnp-data--porting-data" v-if="isMnpToggleActive && getProviderListLoadingStatus === 'LOADED'">
        <porting-data
          ref="portingData"
          :key="1"
          :portingDataMsisdn.sync="portingData.msisdn"
          :portingDataDateType.sync="portingData.dateType"
          :portingDataConfirmation.sync="portingData.confirmation"
        >
          <template v-for="slot in possibleChildSlotsFor('PortingData')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </porting-data>
      </div>
    </form>
    <Stepper
      @next="save()"
      :show-prev="false"
      :class="'no-prev'"
      :aria-label-next="getSlotText('accessibilityNextBtnAltStepper')"
    >
      <template slot="btnNext">
        <slot v-if="active && getProviderListLoadingStatus === 'LOADED'" name="btnNextMnpStepper"></slot>
        <slot v-else name="btnNextStepper"></slot>
      </template>
    </Stepper>
  </div>
</template>

<script>
import preFilledInput from '@/mixins/preFilledInput'
import preFilledObjectInput from '@/mixins/preFilledObjectInput'
import isValid from '@/mixins/validators/isValid'
import validationFlow from '@/mixins/validators/validationFlow'
import LabeledCustomSwitch from '@/components/forms/LabeledCustomSwitch.vue'
import Stepper from '@/components/generic/Stepper.vue'
import PortingData from '@/components/checkout/mnpData/PortingData.vue'
import { mapActions } from 'vuex'
import ProviderSelect from '@/components/checkout/mnpData/ProviderSelect.vue'
import trackingHandler from '@/mixins/trackingHandler'

export default {
  name: 'MnpData',
  data () {
    return {
      invalid: {
        provider: true
      },
      preselectProvider: ''
    }
  },
  props: {
    process: {
      type: String,
      default: 'ordering'
    }
  },
  async created () {
    await this.getProviderList()
  },
  mixins: [
    isValid,
    preFilledInput('active', 'getActive'),
    preFilledObjectInput('provider', 'getProvider'),
    preFilledObjectInput('portingData', 'getPortingData'),
    validationFlow,
    trackingHandler
  ],
  components: {
    ProviderSelect,
    PortingData,
    Stepper,
    LabeledCustomSwitch
  },
  methods: {
    // legacy store
    ...mapActions('checkout/mnpData', {
      legacyUpdateActive: 'updateActive',
      legacyUpdateProvider: 'updateProvider'
    }),
    setPortingData (portingData) {
      this.$store.commit(`checkoutv2/${this.process}/_mnpData/setPortingData`, portingData)
    },
    setActive (status) {
      this.$store.commit(`checkoutv2/${this.process}/_mnpData/setActive`, status)
    },
    setProvider (provider) {
      this.$store.commit(`checkoutv2/${this.process}/_mnpData/setProvider`, provider)
    },
    getProviderList () {
      this.$store.dispatch(`checkoutv2/${this.process}/_mnpData/getProviderList`)
    },
    storeSelectedProvider () {
      this.setProvider(this.provider)
      this.$emit('updatedProvider')
      // update legacy store provider
      this.legacyUpdateProvider({
        active: this.getActive,
        provider: this.provider
      })
    },
    save () {
      // ensure live feedback for validation errors
      if (this.$refs.portingData) {
        this.$refs.portingData.forceValidationOnNestedInputs()
      }
      if (this.$refs.providerSelect) {
        this.$refs.providerSelect.vflowForceFeedback()
      }

      // check if form validation was successful
      if (!this.active || (!this.invalid.provider && !this.$refs.portingData.isFormInvalid)) {
        this.setPortingData(this.portingData)
        this.clearActiveToasts()
        this.$emit('goForward')
      } else {
        this.vFlowScrollToFirstElemment()
        const _this = this
        this.vFlowGetFirstErrorElementName().then(function (result) {
          _this.triggerValidationErrorTracking(result)
        })
      }
    },
    clearActiveToasts () {
      this.clearToasts()
    }
  },
  computed: {
    getActive () {
      return this.$store.getters[`checkoutv2/${this.process}/_mnpData/active`] === true
    },
    getProvider () {
      return this.$store.getters[`checkoutv2/${this.process}/_mnpData/provider`]
    },
    getPortingData () {
      return this.$store.getters[`checkoutv2/${this.process}/_mnpData/portingData`]
    },
    getProviderListLoadingStatus () {
      return this.$store.getters[`checkoutv2/${this.process}/_mnpData/providerListLoadingStatus`]
    },
    isMnpToggleActive: {
      get: function () {
        this.clearActiveToasts()
        return this.active
      },
      set: function (newValue) {
        this.active = !this.active
        this.setActive(newValue)
        this.legacyUpdateActive({ active: newValue })
        this.$emit('updatedMnpActive')
      }
    },
    quickSelectionBrands () {
      return this.getSlotText('txtBrand1CommonProviderSelect') + ',' + this.getSlotText('txtBrand2CommonProviderSelect') + ',' + this.getSlotText('txtBrand3CommonProviderSelect') + ',' + this.getSlotText('txtBrand4CommonProviderSelect') + ',' + this.getSlotText('txtBrand5CommonProviderSelect')
    }
  },
  watch: {
    // On first change of active, loader is appearing and focus has to be set to the first input field. Also the text is hidden and shown again to make the screenreader read the text again after loader is gone.
    active: function (val, oldVal) {
      if (val !== oldVal) {
        this.stopLoader(() => {
          document.getElementById('mnpData')?.focus()
        })
      }
    }
  }
}
</script>
